import { create } from "zustand"
import { persist } from "zustand/middleware"

export interface Coordinates {
  latitude: number
  longitude: number
}

export interface Address {
  full: string
  display: string
  placeId?: string
}

export interface LocationState {
  coordinates: Coordinates | null
  address: Address | null
  isLoading: boolean
  error: string | null
  lastUpdated: number | null

  // Actions
  setCoordinates: (coordinates: Coordinates | null) => void
  setAddress: (address: Address | null) => void
  setLoading: (isLoading: boolean) => void
  setError: (error: string | null) => void
  reset: () => void
  checkExpiration: () => void
}

// 24 hours in milliseconds
const EXPIRATION_TIME = 24 * 60 * 60 * 1000

export const useLocationStore = create<LocationState>()(
  persist(
    (set, get) => ({
      coordinates: null,
      address: null,
      isLoading: false,
      error: null,
      lastUpdated: null,

      setCoordinates: (coordinates) =>
        set({
          coordinates,
          lastUpdated: coordinates ? Date.now() : null,
        }),

      setAddress: (address) =>
        set({
          address,
          lastUpdated: address ? Date.now() : null,
        }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error, isLoading: false }),

      reset: () =>
        set({
          coordinates: null,
          address: null,
          isLoading: false,
          error: null,
          lastUpdated: null,
        }),

      checkExpiration: () => {
        const state = get()
        const now = Date.now()

        if (state.lastUpdated && now - state.lastUpdated > EXPIRATION_TIME) {
          get().reset()
        }
      },
    }),
    {
      name: "location-storage",
      partialize: (state) => ({
        coordinates: state.coordinates,
        address: state.address,
        lastUpdated: state.lastUpdated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.checkExpiration()
        }
      },
    }
  )
)
